// LES ACCESSOIRES DE TÊTE (26/07, second volet du choix « les deux, tenues d'abord »).
//
// Une tenue change les couleurs ; un accessoire change la SILHOUETTE — c'est ce qui se reconnaît
// de loin, et ce qu'un enfant montre du doigt. Le dessin vit dans client/baroudeur.ts (canvas) ;
// ici, seul le catalogue, PUR et partagé, pour que le serveur et les tests puissent le lire.
//
// Ancrage : chaque pose du Baroudeur expose déjà sa hauteur tête-pieds (refH) et son point de
// pieds. Le sommet du crâne s'en déduit exactement, dans le repère NATIF de la pose — l'accessoire
// est donc dessiné avant la mise à l'échelle et suit le héros partout, miroir compris, sans
// réglage par pose. Restent quelques décalages fins pour les poses penchées (voir HEAD_NUDGE).

export interface Accessoire {
  /** Identifiant, aussi celui du loot équipable (slot 'hat'). */
  id: string
  name: string
  desc: string
  /**
   * Vrai si l'accessoire REMPLACE la calotte du Baroudeur au lieu de se poser dessus.
   *
   * ⚠️ C'est LE point qui décide si un chapeau a l'air d'appartenir au personnage. Le Baroudeur
   * porte déjà un couvre-chef : calotte beige, bandeau turquoise, lampe frontale et visière. Un
   * casque posé par-dessus donne deux chapeaux empilés — le défaut vu par Claude-Alain sur le
   * casque à cornes. Quand `remplace` est vrai, la calotte est repeinte en CHEVEUX (les lettres
   * d/D du sprite) et l'accessoire prend sa place ; le bandeau, la lampe et la visière restent,
   * parce qu'ils font l'identité du personnage.
   *
   * Faux pour ce qui ne couvre pas la tête (l'auréole flotte au-dessus).
   */
  remplace: boolean
}

export const ACCESSOIRES: readonly Accessoire[] = [
  { remplace: true, id: 'chapeau-paille', name: 'Chapeau de paille', desc: 'Un grand bord tressé, pour le soleil qu’on ne voit jamais.' },
  { remplace: true, id: 'chapeau-bonnet', name: 'Bonnet à pompon', desc: 'Rouge et chaud, avec un pompon qui rebondit.' },
  { remplace: true, id: 'chapeau-cornes', name: 'Casque à cornes', desc: 'Deux cornes ivoire sur un casque de fer.' },
  { remplace: true, id: 'chapeau-hautdeforme', name: 'Haut-de-forme', desc: 'Noir et lustré, pour les grandes occasions.' },
  { remplace: false, id: 'chapeau-aureole', name: 'Auréole', desc: 'Un anneau de lumière qui flotte au-dessus de toi.' },
  { remplace: true, id: 'chapeau-couronne', name: 'Couronne', desc: 'De l’or, cinq pointes, et le regard des autres.' },
  // Seconde fournée (26/07). `remplace` tranché pièce par pièce : tout ce qui COUVRE le crâne le
  // met à vrai (sinon on empile deux couvre-chefs, le défaut vu sur le casque à cornes) ; la
  // couronne de fleurs, elle, n'est qu'un bandeau tressé et se pose par-dessus la calotte.
  { remplace: true, id: 'chapeau-oreilles', name: 'Oreilles de chat', desc: 'Deux oreilles qui pointent, et tout le monde sourit.' },
  { remplace: false, id: 'chapeau-fleurs', name: 'Couronne de fleurs', desc: 'Cinq fleurs tressées, cueillies on ne sait où.' },
  { remplace: true, id: 'chapeau-tricorne', name: 'Tricorne', desc: 'Feutre bleu nuit, galon d’or et plume rouge.' },
  { remplace: true, id: 'chapeau-bois', name: 'Bois de cerf', desc: 'Deux ramures qui te grandissent d’un demi-mètre.' },
  { remplace: true, id: 'chapeau-sorcier', name: 'Chapeau de sorcier', desc: 'Un grand cône violet, une étoile au bout.' },
  { remplace: true, id: 'chapeau-ailes', name: 'Casque ailé', desc: 'Deux ailes d’argent, un bandeau d’or.' },
  // Troisième fournée (30/07), validée sur /pages/cosmetiques.html. ⚠️ Chaque ajout ICI doit
  // arriver AVEC sa forme dans le `formes` de chapeau4 (client/papier.ts) : le Casque ailé est
  // resté deux jours sans dessin parce que les trois tests de ce fichier ne vérifiaient que la
  // concordance des CATALOGUES. Une garde le refuse désormais.
  { remplace: true, id: 'chapeau-helice', name: 'Casquette à hélice', desc: 'Quatre pales sur le crâne, et l’air d’y croire.' },
  { remplace: true, id: 'chapeau-toque', name: 'Toque de cuisinier', desc: 'Haute, blanche, bombée. On ne sait pas ce qu’elle fait là.' },
  { remplace: true, id: 'chapeau-heaume', name: 'Heaume de chevalier', desc: 'Acier, fente au regard, plumet rouge.' },
  // Le nuage FLOTTE : il se cumule avec le casque de mineur au lieu de le remplacer.
  { remplace: false, id: 'chapeau-orage', name: 'Nuage d\'orage', desc: 'Un petit orage personnel qui te suit partout.' },
  // ÉLU DE LA VAGUE 2 (13/08) : le seul chapeau des douze qu'il a retenu.
  { remplace: true, id: 'chapeau-lampe', name: 'Lampe de mineur', desc: 'Le vrai casque du métier, avec sa lampe qui balaie devant toi.' },
]

const PAR_ID = new Map(ACCESSOIRES.map((a) => [a.id, a]))
export function accessoireById(id: string): Accessoire | undefined { return PAR_ID.get(id) }
